import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../app/hooks";
import { useTranslation } from "react-i18next";
import { selectCurrentUser } from "../../Users/usersSlice";
import { useAssessmentRoleRequest } from "../assessmentUtils";
import { useEffect, useRef, useState } from "react";
import { Mixpanel } from "../../Analytics/Mixpanel";
import { MixpanelEvents } from "../../../app/constants";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography
} from "@mui/material";
import { APIError } from "../../../common-ui/APIError";
import { PageWrapper, PageWrapperProps } from "../../../common-ui/PageWrapper";
import { CreateAssessmentEvent } from "./CreateAssessmentEvent";
import { AssessmentRole } from "../../../API";
import { useAvailableParticipants } from "../../Users/userUtils";
import { AvailableParticipantsInput } from "../../Users/usersAPI";

const pageDetails: PageWrapperProps = {
  className: "AssessmentRoles"
};

export const AssessmentRoles = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getAvailableParticipants } = useAvailableParticipants();
  const didFetchRef = useRef(false);

  const { assessmentRole, status, error } = useAssessmentRoleRequest(
    currentUser?.id
  );

  useEffect(() => {
    Mixpanel.track(MixpanelEvents.HomePageViewed);
    setFinalizedRole(null);
    setSelectedOption(null);
    didFetchRef.current = false;
  }, []);

  const [selectedOption, setSelectedOption] = useState<AssessmentRole | null>(
    null
  );
  const [finalizedRole, setFinalizedRole] = useState<AssessmentRole | null>(
    null
  );

  const location = useLocation();

  useEffect(() => {
    setFinalizedRole(null);
    setSelectedOption(null);
  }, [location.key]);

  useEffect(() => {
    const shouldAutoRedirect =
      status === "succeeded" &&
      assessmentRole?.assessmentRole &&
      !assessmentRole?.showPrompt;
    // Only trigger auto-finalization *after* initial reset
    if (
      shouldAutoRedirect &&
      finalizedRole === null &&
      assessmentRole?.assessmentRole
    ) {
      setFinalizedRole(assessmentRole.assessmentRole);
    }
  }, [status, assessmentRole, finalizedRole]);

  useEffect(() => {
    if (!didFetchRef.current && currentUser?.id && finalizedRole) {
      const input: AvailableParticipantsInput = {
        userId: currentUser.id,
        assessmentRole: finalizedRole
      };
      getAvailableParticipants(input);
      didFetchRef.current = true;
    }
  }, [finalizedRole, currentUser?.id]);

  const handleNext = () => {
    if (currentUser?.id && selectedOption) {
      didFetchRef.current = false;
      setFinalizedRole(selectedOption);
    }
  };

  if (status === "loading") {
    return <CircularProgress />;
  }

  if (status === "failed")
    return <APIError error={error ?? "Error loading pending assessments."} />;

  if (finalizedRole) {
    return <CreateAssessmentEvent role={finalizedRole} />;
  }

  if (status === "succeeded" && assessmentRole?.showPrompt) {
    return (
      <PageWrapper {...pageDetails}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={8} lg={6}>
            <Grid container direction="column" alignItems="center" spacing={4}>
              <Grid item spacing={2} sx={{ mt: 4 }}>
                <Typography variant="h6" align="center">
                  {t("What would you like to do?")}
                </Typography>
              </Grid>
              <Grid item width="100%">
                <FormControl component="fieldset" fullWidth>
                  <RadioGroup
                    value={selectedOption}
                    onChange={(e) =>
                      setSelectedOption(e.target.value as AssessmentRole)
                    }
                    sx={{ alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        width: "80%",
                        cursor: "pointer"
                      }}
                      onClick={() => setSelectedOption(AssessmentRole.rater)}
                    >
                      <FormControlLabel
                        value="rater"
                        control={<Radio />}
                        label="Create an assessment for somebody else."
                        sx={{ width: "100%", m: 0 }}
                      />
                    </Box>
                    <Box
                      sx={{
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                        width: "80%",
                        cursor: "pointer"
                      }}
                      onClick={() => setSelectedOption(AssessmentRole.subject)}
                    >
                      <FormControlLabel
                        value="subject"
                        control={<Radio />}
                        label="Create an assessment for myself"
                        sx={{ width: "100%", m: 0 }}
                      />
                    </Box>
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item container spacing={3} justifyContent="center">
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/assessments/pending")}
                  >
                    Back
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    disabled={!selectedOption}
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </PageWrapper>
    );
  }
  return (
    <PageWrapper {...pageDetails}>
      <Typography color="error">
        Something went wrong. Please refresh the page.
      </Typography>
    </PageWrapper>
  );
};
